import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DatePicker, Form, Select } from "antd";
import moment from "moment";
import pluralize from "pluralize";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import enums_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Alert, Button, Modal, toast, Tooltip } from "@skydio/rivet-ui";

import { useInviteUsers } from "hooks/mutation/inviteUser";
import { useSelector } from "hooks/redux";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useOrgNodeId } from "hooks/useOrgNodeId";
import { getOrgName, getOrgUuid } from "state/current_user/selectors";
import {
  getReadableOrgRoleFromValue,
  getReadableOrgRoles,
  getResourceGroupDisplayName,
} from "utils/team";

import { OrgPermissionHelpLink } from "./OrgPermissionHelpLink";
import RoleGroupFormSection from "./RoleGroupFormSection";
import { useOrganizationResourceGroups } from "./useOrganizationResourceGroups";

import type { FormProps } from "antd";
import type { Moment } from "moment";
import type { ValueType } from "@skydio/core";
import type {
  inviteUsersMutation,
  ResourceGroupLinkInput,
} from "__generated__/inviteUsersMutation.graphql";
import type { organizationpermission } from "__generated__/upsertUserMutation.graphql";
import type { RoleWithResourceGroups } from "./RoleGroupFormSection";

// very basic validation that should catch invalid emails due to bad separators
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InviteMemberFormFields {
  emails: string[];
  roleWithResourceGroups: RoleWithResourceGroups[];
  expiry: Moment | null;
}

export const defaultRoleWithResourceGroup = {
  rgRole: getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MODERATOR),
  resourceGroupNames: [],
};

const defaultUserRoleGroups = {
  emails: [],
  roleWithResourceGroups: [defaultRoleWithResourceGroup],
  expiry: null,
};

const InviteMember = () => {
  const { t } = useTranslation("team");
  const [form] = Form.useForm();
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(false);

  const orgUuid = useSelector(getOrgUuid);
  const orgId = useOrgNodeId() ?? "";
  const orgName = useSelector(getOrgName);

  const [visible, setVisible] = useState(false);
  const missionsPageEnabled = useFeatureFlag("missionsPage");
  const vehicleTeleopEnabled = useFeatureFlag("vehicleTeleoperation");
  const supportUserExpiryEnabled = useFeatureFlag("supportUserExpiry");
  const showRemotePilotRole = missionsPageEnabled || vehicleTeleopEnabled;

  const readableOrgRoles = getReadableOrgRoles(showRemotePilotRole, supportUserExpiryEnabled);

  const [skydioSupportRole, setSkydioSupportRole] = useState<string | undefined>(undefined);

  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const { resourceGroups } = useOrganizationResourceGroups({ orgUuid });
  const [emailsContainSkydio, setEmailsContainSkydio] = useState(false);

  const onUsersInvited = useCallback(
    (response: inviteUsersMutation["response"]) => {
      const invitedUsers = response.inviteUsers?.invitedUsers;
      const errors = response.inviteUsers?.errors;

      if (invitedUsers) {
        invitedUsers.forEach(email => {
          toast.success(t("inviteModal.success", { email: email }), {
            duration: 3000,
          });
        });
      }

      if (errors) {
        errors.forEach(error => {
          toast.error(error, {
            duration: 3000,
          });
        });
      }
    },
    [t]
  );
  const { inviteUsers } = useInviteUsers(onUsersInvited);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(defaultUserRoleGroups);
    setErrorMessages([]); // clear error messages
  }, [visible, form]);

  const convertReadableOrgRole = useCallback(
    (value: string): ValueType<enums_pb.OrgPermission.PermEnumMap> => {
      const orgPermission = readableOrgRoles.find(
        ({ readableName }) => readableName.toUpperCase() === value.toUpperCase()
      );
      if (orgPermission) {
        return orgPermission.enumValue as ValueType<enums_pb.OrgPermission.PermEnumMap>;
      } else {
        return enums_pb.OrgPermission.PermEnum.MODERATOR;
      }
    },
    [readableOrgRoles]
  );

  const handleFormChanged = useCallback<
    NonNullable<FormProps<InviteMemberFormFields>["onValuesChange"]>
  >(
    (_, allValues) => {
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
      console.log("hasErrors", hasErrors);
      setFormSubmitDisabled(hasErrors);

      const { roleWithResourceGroups } = allValues;
      if (roleWithResourceGroups) {
        const newUserIsSkydioSupport = roleWithResourceGroups.find(
          ({ rgRole }) =>
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
        );
        setSkydioSupportRole(newUserIsSkydioSupport?.rgRole);
      }
      form.validateFields(["expiry"]);
      form.validateFields(["emails"]);
      const emailList: string[] = form.getFieldValue("emails");
      setEmailsContainSkydio(emailList.some(email => email.endsWith("@skydio.com")));
      setErrorMessages([]);
    },
    [form]
  );

  const handleSendInvite = useCallback(
    (formValues: InviteMemberFormFields) => {
      const { emails, roleWithResourceGroups, expiry } = formValues;

      // Collect error messages
      const errorMsgs: string[] = [];

      // Ensure that each group is only mapped to one role (only for Cloud User and Remote Pilot roles)
      const groupToRoleMap = new Map<string, string[]>();
      const groupsWithMultipleRoles = new Set<string>(); // Use a Set to avoid duplicates

      roleWithResourceGroups.forEach(({ rgRole, resourceGroupNames }) => {
        if (
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER)
        )
          return;
        resourceGroupNames.forEach(rgName => {
          if (groupToRoleMap.has(rgName)) {
            const existingRoles = groupToRoleMap.get(rgName);
            if (existingRoles && !existingRoles.includes(rgRole)) {
              groupToRoleMap.set(rgName, existingRoles.concat(rgRole));
              groupsWithMultipleRoles.add(rgName);
            }
          } else {
            groupToRoleMap.set(rgName, [rgRole]);
          }
        });
      });

      // convert to display name for error message if needed
      const groupsWithMultipleRolesArray = Array.from(groupsWithMultipleRoles).map(group =>
        getResourceGroupDisplayName(orgName, group)
      );

      if (groupsWithMultipleRolesArray.length > 0) {
        errorMsgs.push(
          `Only one role may be assigned to ${groupsWithMultipleRolesArray.join(", ")}`
        );
      }

      // Ensure that Pilot and Admin are not both selected at the same time
      const hasAdmin = roleWithResourceGroups.some(
        ({ rgRole }) =>
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN)
      );
      const hasPilot = roleWithResourceGroups.some(
        ({ rgRole }) =>
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER)
      );
      const hasSuperuser = roleWithResourceGroups.some(
        ({ rgRole }) =>
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
      );
      const hasReadOnly = roleWithResourceGroups.some(
        ({ rgRole }) =>
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
      );
      if (
        [hasAdmin, hasPilot, hasSuperuser, hasReadOnly].reduce(
          (acc, val) => acc + (val ? 1 : 0),
          0
        ) > 1
      ) {
        errorMsgs.push(
          "Organization Admin, Pilot, and Skydio roles cannot be assigned at the same time"
        );
      }

      // Halt submission if there are any errors
      if (errorMsgs.length > 0) {
        setErrorMessages(errorMsgs);
        return;
      }

      let orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
        CLOUD_API.modules.organizations.utils.orgPermissionEnum.MEMBER
      );
      if (hasAdmin) {
        orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          CLOUD_API.modules.organizations.utils.orgPermissionEnum.ADMIN
        );
      } else if (hasSuperuser) {
        orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          CLOUD_API.modules.organizations.utils.orgPermissionEnum.SKYDIO_SUPERUSER
        );
      } else if (hasReadOnly) {
        orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          CLOUD_API.modules.organizations.utils.orgPermissionEnum.SKYDIO_READONLY
        );
      } else if (hasPilot) {
        orgLevelPermission = CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
          CLOUD_API.modules.organizations.utils.orgPermissionEnum.MEMBER
        );
      }

      // Convert form inputs to resourceGroupLinks for inviteUsers mutation
      const resourceGroupLinks: ResourceGroupLinkInput[] = [];
      roleWithResourceGroups.forEach(({ rgRole, resourceGroupNames }) => {
        if (
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER) ||
          rgRole ===
            getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER) ||
          rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY)
        ) {
          return;
        }
        resourceGroupNames.forEach(rgName => {
          const resourceGroupId = resourceGroups.find(({ name }) => name === rgName)?.id || "";
          const resourceGroupLevelPermission =
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.REMOTE_PILOT)
              ? CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
                  CLOUD_API.modules.organizations.utils.orgPermissionEnum.REMOTE_PILOT
                )
              : CLOUD_API.modules.organizations.utils.getOrgPermissionKey(
                  CLOUD_API.modules.organizations.utils.orgPermissionEnum.MODERATOR
                );

          if (resourceGroupId) {
            resourceGroupLinks.push({
              resourceGroupNodeId: resourceGroupId,
              resourceGroupPermission: resourceGroupLevelPermission as organizationpermission,
            });
          }
        });
      });
      const parsedExpiry = expiry ? expiry.utc().toISOString() : null;

      inviteUsers(
        orgId,
        emails,
        orgLevelPermission as organizationpermission,
        resourceGroupLinks,
        parsedExpiry
      );
      setSkydioSupportRole(undefined);
      setVisible(false);
    },
    [inviteUsers, orgId, resourceGroups, setVisible, orgName]
  );

  const validateEmails = useCallback(
    async (_: unknown, emails: string[]) => {
      if (!emails) {
        throw new Error("Please input email addresses!");
      }

      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        const errorMessage = `Invalid ${pluralize(
          "email address",
          invalidEmails.length
        )}: ${invalidEmails.join(", ")}`;
        throw new Error(errorMessage);
      }

      if (
        form.getFieldValue("roleWithResourceGroups").some(({ rgRole }: RoleWithResourceGroups) => {
          return (
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
          );
        })
      ) {
        if (emails.some(email => !email.endsWith("@skydio.com"))) {
          throw new Error("Skydio roles can only be assigned to Skydio email addresses");
        }
      }
    },
    [form]
  );

  const validateOrgRole = useCallback(
    async (_: unknown, role: string) => {
      if (!role) {
        throw new Error(t("inviteModal.orgRoleMissingError"));
      } else if (!convertReadableOrgRole(role)) {
        throw new Error(t("inviteModal.orgRoleValidateError", { role }));
      }
    },
    [convertReadableOrgRole, t]
  );

  const validateWarnUnsetExpiry = useCallback(
    async (_: unknown, expiry: Moment | null) => {
      const skydioUserRole: RoleWithResourceGroups | undefined = form
        .getFieldValue("roleWithResourceGroups")
        .find(
          ({ rgRole }: RoleWithResourceGroups) =>
            rgRole ===
              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY) ||
            rgRole === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER)
        );
      if (!expiry && skydioUserRole !== undefined) {
        return Promise.reject(
          <span className="text-gray-500">
            Skydio recommends setting an expiration for all {skydioUserRole.rgRole} users.
          </span>
        );
      }
      return Promise.resolve();
    },
    [form]
  );

  const emailValidators = useMemo(
    () => [
      {
        required: true,
        message: "Please input email addresses!",
      },
      {
        validator: validateEmails,
      },
    ],
    [validateEmails]
  );

  const expiryValidators = useMemo(
    () => [
      {
        validator: (_: unknown, value: Moment) => {
          if (value && value.isBefore(moment())) {
            return Promise.reject("Expiry date must be in the future.");
          }
          return Promise.resolve();
        },
      },
      {
        validator: validateWarnUnsetExpiry,
        warningOnly: true,
      },
    ],
    [validateWarnUnsetExpiry]
  );

  // TODO squash down errors as much as I can

  const setModalVisible = useCallback(() => {
    setVisible(true);
  }, [setVisible]);

  const tokenSeparators = useMemo(() => [","], []);

  const modalClassNames = useMemo(
    () => ({
      base: "max-w-xl",
      wrapper: "z-[1000]",
    }),
    []
  );

  const handleCancelClick = useCallback(() => {
    setSkydioSupportRole(undefined);
    setVisible(false);
  }, [setSkydioSupportRole, setVisible]);

  return (
    <>
      <Button variant="primary" onPress={setModalVisible}>
        Invite User
      </Button>
      <Modal isOpen={visible} hideCloseButton classNames={modalClassNames}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Invite User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Display error messages after form submission if present */}
            {errorMessages.length > 0 && (
              <div className="mb-4">
                {errorMessages.map((errorMessage, index) => (
                  <Alert key={index} status="error">
                    <Alert.Content>
                      <Alert.Title>{errorMessage}</Alert.Title>
                    </Alert.Content>
                  </Alert>
                ))}
              </div>
            )}
            {/* User emails input */}
            <Form
              form={form}
              name="dynamic_form_nest_item_2"
              onFinish={handleSendInvite}
              initialValues={defaultUserRoleGroups}
              onValuesChange={handleFormChanged}
            >
              <div className="mb-2 font-semibold text-legacyGray-900">Email</div>
              <div className="pb-1">
                <Form.Item name="emails" rules={emailValidators}>
                  <Select
                    mode="tags"
                    className="w-full"
                    placeholder="Add emails separated by commas"
                    tokenSeparators={tokenSeparators}
                    open={false}
                  />
                </Form.Item>
              </div>
              {supportUserExpiryEnabled && (
                <>
                  <div className="mb-2 font-semibold text-legacyGray-900">
                    {"Expiration "}
                    <Tooltip content="Optional: Set this to automatically disable user after given date & time.">
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </Tooltip>
                  </div>
                  <div className="pb-1">
                    <Form.Item name="expiry" rules={expiryValidators}>
                      <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                    </Form.Item>
                  </div>
                </>
              )}
              <RoleGroupFormSection
                form={form}
                orgName={orgName}
                resourceGroups={resourceGroups}
                validateOrgRole={validateOrgRole}
                convertReadableOrgRole={convertReadableOrgRole}
                showPermissionSummary
                showSkydioRoles={emailsContainSkydio}
              />
              {supportUserExpiryEnabled && skydioSupportRole && (
                <Alert status="warning">
                  <Alert.Content>
                    <Alert.Title>This user will have Skydio internal permissions</Alert.Title>
                    <Alert.Description>
                      <OrgPermissionHelpLink />
                    </Alert.Description>
                  </Alert.Content>
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="default" onClick={handleCancelClick} ghost>
              Cancel
            </Button>
            <Button variant="primary" onClick={form.submit} isDisabled={formSubmitDisabled}>
              Send Invite
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default InviteMember;
